import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FileText,
  Download,
  Loader2,
} from 'lucide-react';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { reportsApi } from '@/services/reportsApi';
import type { PagedTimesheetReportResponse, ReportSummaryDto } from '@/services/reportsApi';
import { rejectionsApi } from '@/services/rejectionsApi';
import { TimesheetReportGrid } from '@/components/TimesheetReportGrid';
import { AdvancedFilterDrawer } from '@/components/TimesheetReports/AdvancedFilterDrawer';
import { QuickFilters } from '@/components/TimesheetReports/QuickFilters';
import { ActiveFilterChips } from '@/components/TimesheetReports/ActiveFilterChips';
import { transformTimesheetsToReportEntries, getReportSummary } from '@/utils/timesheetReportUtils';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { toast } from 'sonner';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: string;
  weekEnding: Date;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
};

// Main Timesheet Reports Page Component
interface TimesheetReportsPageProps {
  onNavigate?: (path: string, state?: AppNavigationState) => void;
}

export const TimesheetReportsPage: React.FC<TimesheetReportsPageProps> = ({ onNavigate }) => {
  const { timesheets, employees, projects, costCentres } = useTimesheet();
  
  // Advanced Filter state
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filters, setFilters] = useState<TimesheetReportFilters>({});

  const { mode, isDemo, isLive } = useScreenDataMode('reports-timesheet');

  const [liveReportData, setLiveReportData] = useState<PagedTimesheetReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLive) {
      fetchLiveReportData();
    }
  }, [isLive]);

  const fetchLiveReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getTimesheetReport({
        includeSummary: true,
        pageNumber: 1,
        pageSize: 1000, // Fetch a large number for initial implementation
        sortBy: 'weekEnding',
        sortDir: 'desc'
      });
      setLiveReportData(response);
    } catch (err) {
      const errorMessage = 'Failed to load report data from API';
      setError(errorMessage);
      console.error('Error fetching live report data:', err);
      toast.error('Failed to load report data', {
        description: 'Could not connect to the API. Please ensure the backend is running or switch to demo mode.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Get all timesheets (not just current employee's) - used for demo mode and cards view
  const allTimesheets = useMemo(() => {
    return timesheets
      .sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime());
  }, [timesheets]);

  // Transform timesheets to report entries (demo mode)
  const demoReportEntries = useMemo(() => {
    return transformTimesheetsToReportEntries(allTimesheets, employees, projects);
  }, [allTimesheets, employees, projects]);

  // Use live data when available, otherwise use demo data
  const reportEntries = useMemo(() => {
    if (isLive && liveReportData) {
      return liveReportData.items;
    }
    return demoReportEntries;
  }, [isLive, liveReportData, demoReportEntries]);

  // Apply advanced filters to report entries
  const filteredReportEntries = useMemo(() => {
    return reportEntries.filter((entry) => {
      // Date range filter
      if (filters.dateFrom && entry.weekEnding < filters.dateFrom) return false;
      if (filters.dateTo && entry.weekEnding > filters.dateTo) return false;
      
      // Employee filter
      if (filters.employeeIds && filters.employeeIds.length > 0 && 
          !filters.employeeIds.includes(entry.employeeId)) return false;
      
      // Status filter
      if (filters.statuses && filters.statuses.length > 0 && 
          !filters.statuses.includes(entry.status)) return false;
      
      // Department filter
      if (filters.departments && filters.departments.length > 0 && 
          !filters.departments.includes(entry.department)) return false;
      
      // Project filter (using projectCode as identifier)
      if (filters.projectIds && filters.projectIds.length > 0 && 
          !filters.projectIds.includes(entry.projectCode)) return false;
      
      // Billable filter
      if (filters.billableOnly && !entry.billable) return false;
      
      // Rejected filter
      if (filters.rejectedOnly && !entry.hasRejections) return false;
      
      return true;
    });
  }, [reportEntries, filters]);

  // Get report summary (based on all data - filtering will be handled by grid)
  const reportSummary = useMemo(() => {
    if (isLive && liveReportData?.summary) {
      return liveReportData.summary;
    }
    return getReportSummary(demoReportEntries);
  }, [isLive, liveReportData, demoReportEntries]);

  // Handle row click to navigate to timesheet detail
  const handleRowClick = (entry: any) => {
    if (onNavigate) {
      // Navigate to Timesheet Grid with the selected employee and week
      onNavigate('/timesheet', {
        timesheetDrilldown: {
          employeeId: entry.employeeId,
          weekEnding: entry.weekEnding
        }
      });
    }
  };

  // Handle CSV export
  const handleExport = () => {
    try {
      // Create CSV header
      const headers = [
        'Employee',
        'Week Ending',
        'Status',
        'Project',
        'Cost Centre',
        'Description',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'Total Hours',
        'Billable'
      ];

      // Create CSV rows (export all data - user can filter in grid before exporting)
      const rows = reportEntries.map(entry => [
        entry.employeeName,
        format(entry.weekEnding, 'yyyy-MM-dd'),
        entry.status,
        entry.projectName,
        entry.costCentreName,
        entry.description || '',
        entry.mondayHours.toFixed(2),
        entry.tuesdayHours.toFixed(2),
        entry.wednesdayHours.toFixed(2),
        entry.thursdayHours.toFixed(2),
        entry.fridayHours.toFixed(2),
        entry.saturdayHours.toFixed(2),
        entry.sundayHours.toFixed(2),
        entry.totalHours.toFixed(2),
        entry.billable ? 'Yes' : 'No'
      ]);

      // Add summary row
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Hours', reportSummary.totalHours.toFixed(2)]);
      rows.push(['Billable Hours', reportSummary.billableHours.toFixed(2)]);
      rows.push(['Billable %', `${reportSummary.billablePercentage.toFixed(1)}%`]);
      rows.push(['Total Entries', reportSummary.totalEntries.toString()]);
      rows.push(['Unique Projects', reportSummary.uniqueProjects.toString()]);
      rows.push(['Entries with Rejections', reportSummary.entriesWithRejections.toString()]);

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export Successful', {
        description: `Exported ${reportEntries.length} timesheet entries to CSV`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export Failed', {
        description: 'There was an error exporting the report',
      });
    }
  };

  // Prepare data for Advanced Filter Dialog
  const availableEmployees = useMemo(() => {
    return employees.map(emp => ({
      id: emp.id,
      name: emp.name,
      department: emp.department,
    }));
  }, [employees]);

  const availableDepartments = useMemo(() => {
    return Array.from(new Set(employees.map(emp => emp.department))).sort();
  }, [employees]);

  const availableProjects = useMemo(() => {
    return projects.map(proj => ({
      id: proj.id,
      code: proj.code,
      name: proj.name,
    }));
  }, [projects]);

  const availableCostCentres = useMemo(() => {
    return costCentres.map(cc => ({
      id: cc.id,
      code: cc.code,
      name: cc.name,
    }));
  }, [costCentres]);

  // Count active filters (only from advanced drawer, not quick filters)
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    // Only count filters that are NOT in quick filters
    if (filters.departments && filters.departments.length > 0) count++;
    if (filters.billableOnly) count++;
    if (filters.rejectedOnly) count++;
    // Count date range only if it's a custom range (not from quick filter)
    if ((filters.dateFrom || filters.dateTo) && !filters.quickDateRange) count++;
    return count;
  }, [filters]);

  // Handle removing individual filter
  const handleRemoveFilter = (filterKey: string, value?: string) => {
    const newFilters = { ...filters };
    
    switch (filterKey) {
      case 'dateRange':
        delete newFilters.dateFrom;
        delete newFilters.dateTo;
        delete newFilters.quickDateRange;
        break;
      case 'statuses':
        if (value && newFilters.statuses) {
          newFilters.statuses = newFilters.statuses.filter(s => s !== value);
          if (newFilters.statuses.length === 0) delete newFilters.statuses;
        }
        break;
      case 'employeeIds':
        if (value && newFilters.employeeIds) {
          newFilters.employeeIds = newFilters.employeeIds.filter(id => id !== value);
          if (newFilters.employeeIds.length === 0) delete newFilters.employeeIds;
        }
        break;
      case 'departments':
        if (value && newFilters.departments) {
          newFilters.departments = newFilters.departments.filter(d => d !== value);
          if (newFilters.departments.length === 0) delete newFilters.departments;
        }
        break;
      case 'projectIds':
        if (value && newFilters.projectIds) {
          newFilters.projectIds = newFilters.projectIds.filter(id => id !== value);
          if (newFilters.projectIds.length === 0) delete newFilters.projectIds;
        }
        break;
      case 'billableOnly':
        delete newFilters.billableOnly;
        break;
      case 'rejectedOnly':
        delete newFilters.rejectedOnly;
        break;
    }
    
    setFilters(newFilters);
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setFilters({});
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Timesheet Reports
          </h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive timesheet analysis and reporting for all users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleExport} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Quick Filters */}
      <QuickFilters
        filters={filters}
        onFiltersChange={setFilters}
        onAdvancedClick={() => setShowAdvancedFilter(true)}
        availableEmployees={availableEmployees}
        availableProjects={availableProjects}
        advancedFilterCount={advancedFilterCount}
      />

      {/* Active Filter Chips */}
      <ActiveFilterChips
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
        availableEmployees={availableEmployees}
        availableProjects={availableProjects}
        availableDepartments={availableDepartments}
      />

      {/* Data Grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Timesheet Data</CardTitle>
              <CardDescription>
                Showing {filteredReportEntries.length} of {reportEntries.length} {reportEntries.length === 1 ? 'entry' : 'entries'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLive && loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading report data...</p>
              </div>
            </div>
          )}
          
          {isLive && error && !loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                <Button onClick={fetchLiveReportData} variant="outline" size="sm">
                  Retry
                </Button>
              </div>
            </div>
          )}
          
          {(!isLive || (!loading && !error)) && (
            <TimesheetReportGrid
              data={filteredReportEntries}
              isManager={false}
              onRowClick={handleRowClick}
            />
          )}
        </CardContent>
      </Card>

      {/* Advanced Filter Drawer */}
      <AdvancedFilterDrawer
        open={showAdvancedFilter}
        onOpenChange={setShowAdvancedFilter}
        filters={filters}
        onFiltersChange={setFilters}
        availableEmployees={availableEmployees}
        availableDepartments={availableDepartments}
        availableProjects={availableProjects}
        availableCostCentres={availableCostCentres}
        previewCount={filteredReportEntries.length}
        previewData={{
          totalHours: filteredReportEntries.reduce((sum, entry) => sum + entry.totalHours, 0),
          billableHours: filteredReportEntries.reduce((sum, entry) => entry.billable ? sum + entry.totalHours : sum, 0),
          billablePercentage: filteredReportEntries.length > 0 
            ? (filteredReportEntries.reduce((sum, entry) => entry.billable ? sum + entry.totalHours : sum, 0) / 
               filteredReportEntries.reduce((sum, entry) => sum + entry.totalHours, 0)) * 100 
            : 0,
        }}
      />
    </div>
  );
};
